import React from "react";
import { useHistory } from "react-router-dom";
import PortalHeading from "./components/PortalHeading";
import SubmitterTypeSection from "./components/SubmitterTypeSection";
import RetailSection from "./components/RetailSection";
import CustomerSection from "./components/CustomerSection";
import PrimaryEmailSection from "./components/PrimaryEmailSection";
import ProductSection from "./components/ProductSection";
import FaultSection from "./components/FaultSection";
import AttachmentsSection from "./components/AttachmentsSection";
import UploadRows from "./components/UploadRows";
import TermsSection from "./components/TermsSection";
import Button from "./ui/Button";
import useRequestForm from "./useRequestForm";
import { CUSTOMER_UPLOAD_CATEGORIES, UPLOAD_CATEGORIES } from "./options";
import "./NewCase.css";

/*
RMA Portal - "new case" intake form, reused inside the admin dashboard so
staff can lodge a request on a customer's or retailer's behalf without
leaving the app.

Ported from the standalone customer-portal prototype: same two-page flow and
validation rules, rebuilt on this project's plain JS/CSS + protectedFetch
instead of the prototype's TypeScript/Tailwind/react-query stack.
*/
const NewCase = function (props) {
  const { protectedFetch } = props;
  const history = useHistory();
  const {
    form,
    uploads,
    addUploads,
    removeUpload,
    setUploadCategory,
    isCustomer,
    setSubmitterType,
    claimIsOther,
    porRequired,
    canSubmit,
    isSubmitting,
    submitError,
    submit,
    step,
    page1Attempted,
    canProceedToPage2,
    goToNextPage,
    goToPreviousPage,
  } = useRequestForm(protectedFetch);

  return (
    <div className="rma-nc-root">
      <PortalHeading
        title="Submit a Service Request"
        subtitle="Complete the form below to submit your request. You’ll receive a reference number upon submission."
        onBack={() => history.push("/ticket-new")}
      />

      <div className="rma-nc-stack">
        {step === 1 ? (
          <>
            <SubmitterTypeSection
              value={form.values.submitterType}
              onChange={setSubmitterType}
            />

            {form.values.submitterType !== null && (
              <>
                {/* Whoever is lodging the request fills in their own card first. */}
                {isCustomer ? (
                  <>
                    <CustomerSection
                      form={form}
                      protectedFetch={protectedFetch}
                      attempted={page1Attempted}
                      isCustomer
                    />
                    <RetailSection
                      form={form}
                      protectedFetch={protectedFetch}
                      attempted={page1Attempted}
                      isCustomer
                    />
                  </>
                ) : (
                  <>
                    <RetailSection
                      form={form}
                      protectedFetch={protectedFetch}
                      attempted={page1Attempted}
                    />
                    <CustomerSection
                      form={form}
                      protectedFetch={protectedFetch}
                      attempted={page1Attempted}
                    />
                    <PrimaryEmailSection form={form} attempted={page1Attempted} />
                  </>
                )}

                <ProductSection
                  form={form}
                  protectedFetch={protectedFetch}
                  claimIsOther={claimIsOther}
                  porRequired={porRequired}
                  attempted={page1Attempted}
                  isCustomer={isCustomer}
                />

                {page1Attempted && !canProceedToPage2 && (
                  <p className="rma-nc-warning-banner">
                    Please complete the Retail, Customer and Product sections before continuing.
                  </p>
                )}

                <div className="rma-nc-actions-row">
                  <Button size="lg" onClick={goToNextPage}>
                    Next
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <FaultSection
              form={form}
              onRecordingAdded={(files) => addUploads(files, "Fault photo / video")}
            />

            <AttachmentsSection
              dropzoneTitle="Drag & drop or browse"
              dropzoneHint="Photos or videos of the fault, receipt / proof of purchase"
              description={
                isCustomer
                  ? "Upload your Proof of Purchase (POP), plus any fault photos/videos, then label each file below."
                  : "Upload your Proof of Purchase (POP) and Proof of Return (POR), plus any fault photos/videos, then label each file below."
              }
              accept="image/*,video/*,.pdf"
              onFiles={addUploads}
              aside={
                <span className="rma-nc-attachments-badge">
                  {isCustomer ? "POP required" : <>POP &amp; POR required</>}
                </span>
              }
            >
              <UploadRows
                uploads={uploads}
                categories={isCustomer ? CUSTOMER_UPLOAD_CATEGORIES : UPLOAD_CATEGORIES}
                onCategoryChange={setUploadCategory}
                onRemove={removeUpload}
              />
            </AttachmentsSection>

            <TermsSection
              agreed={form.values.agreed}
              onAgreedChange={(agreed) => form.setField("agreed", agreed)}
              canSubmit={canSubmit}
              isSubmitting={isSubmitting}
              submitError={submitError}
              onSubmit={submit}
              onBack={goToPreviousPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default NewCase;
